import type { AxiosInstance } from 'axios';
import firebase from 'utils/firebase';
import rawServer from 'utils/server';

const server: AxiosInstance = rawServer;

/** Staff view of a payment request as returned by /v03/paymentRequests. */
export type PaymentRequestLine = {
  index: number;
  description: string;
  quantity: number;
  unitPriceCents: number;
  totalCents: number;
  vatRate: string;
  vatCents: number;
};

export type PaymentRequestVatGroup = {
  vatRate: string;
  grossCents: number;
  vatCents: number;
  netCents: number;
};

export type PaymentRequestStatus =
  | 'open'
  | 'processing'
  | 'expired'
  | 'paid'
  | 'partially_refunded'
  | 'refunded'
  | 'revoked';

export type PaymentRequestClarification = {
  reason:
    | 'reservation_cancelled'
    | 'late_payment'
    | 'duplicate_payment'
    | 'amount_mismatch'
    | 'provider_unclear';
  attemptId: string | null;
  at: number;
  resolvedAt: number | null;
  resolvedBy: string | null;
  note: string | null;
};

export type PaymentRequestDelivery = {
  id: string;
  linkVersion: number;
  channel: 'email' | 'sms' | 'copy';
  kind: 'link' | 'confirmation';
  to: string;
  status: 'queued' | 'sending' | 'sent' | 'failed' | 'unknown';
  createdAt: number;
  createdBy: string;
  sentAt: number | null;
  error: string | null;
};

export type PaymentRequestRefund = {
  id: string;
  attemptId: string;
  amountCents: number;
  allocations: { lineIndex: number; amountCents: number }[];
  status: string;
  createdAt: number;
  createdBy: string;
  error: string | null;
  note: { status: 'none' | 'created' | 'failed'; number: string | null; error: string | null };
};

export type PaymentRequestAttempt = {
  id: string;
  number: number;
  linkVersion: number;
  status: string;
  amountCents: number;
  providerAmountCents: number | null;
  molliePaymentId: string | null;
  createdAt: number;
  paidAt: number | null;
  error: string | null;
  refundedCents: number;
};

export type PaymentRequest = {
  id: string;
  reservationId: string;
  kind: 'deposit' | 'payment';
  purpose: string;
  message: string;
  payer: { name: string; email?: string; phone?: string; language: 'de' | 'en' };
  payerIsReservationGuest: boolean;
  invoiceRecipient: Record<string, any> | null;
  testmode: boolean;
  createdAt: number;
  createdBy: string;
  status: PaymentRequestStatus;
  expiresAt: number | null;
  expiryShortenedByReservation: boolean;
  linkVersion: number;
  items: { lines: PaymentRequestLine[]; vatGroups: PaymentRequestVatGroup[]; totalCents: number };
  itemVersions: { version: number; createdAt: number; totalCents: number }[];
  paidCents: number;
  paidAt: number | null;
  refundedCents: number;
  pendingRefundCents: number;
  refundable: { lineIndex: number; remainingCents: number }[];
  /** Over the requested amount, e.g. a request paid twice; to clarify or refund. */
  overpaidCents: number;
  /** Every confirmed provider payment, the primary one first. */
  payments: {
    attemptId: string;
    molliePaymentId: string | null;
    paidCents: number;
    paidAt: number | null;
    refundedCents: number;
    primary: boolean;
    refundable: { lineIndex: number; remainingCents: number }[];
  }[];
  /**
   * Confirmed by hand by the team; Gastronaut has no POS integration and
   * verifies nothing. Older markers carry no reference: unverified.
   */
  credit: { creditedAt: number; creditedBy: string; posReference?: string; amountCents?: number } | null;
  /** Primary payment minus its settled refunds: what a manual credit covers (deposits only). */
  creditableCents: number;
  clarifications: PaymentRequestClarification[];
  needsClarification: boolean;
  deliveries: PaymentRequestDelivery[];
  attempts: PaymentRequestAttempt[];
  refunds: PaymentRequestRefund[];
  actions: { send: boolean; renew: boolean; revoke: boolean; refund: boolean; credit: boolean };
};

export type LineInput = {
  description: string;
  quantity: number;
  unitPriceCents: number;
  vatRate: string;
};

export type CreatePaymentRequestInput = {
  clientRequestId: string;
  kind: 'deposit' | 'payment';
  purpose: string;
  message?: string;
  invoiceRecipient?: { name?: string; company?: string; email?: string };
  payer: {
    useReservationGuest: boolean;
    name?: string;
    email?: string;
    phone?: string;
    language?: 'de' | 'en';
  };
  lines: LineInput[];
  expiresAt?: number | null;
  channel?: 'email' | 'sms' | 'none';
};

const errorMessage = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  if (typeof data?.message === 'string') return data.message;
  if (typeof data === 'string' && data.length < 300) return data;
  return fallback;
};

export class PaymentRequestError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}

const authHeaders = async () => {
  const token = await firebase.auth().currentUser?.getIdToken();
  if (!token) throw new PaymentRequestError('Nicht authentifiziert');
  return { Authorization: `Bearer ${token}` };
};

const base = (restaurantId: string) => `/v03/paymentRequests/${restaurantId}`;

async function request<T>(
  method: 'get' | 'post',
  path: string,
  body: unknown,
  fallback: string
): Promise<T> {
  try {
    const headers = await authHeaders();
    const { data } =
      method === 'get'
        ? await server.get<T>(path, { headers })
        : await server.post<T>(path, body ?? {}, { headers });
    return data;
  } catch (err: any) {
    if (err instanceof PaymentRequestError) throw err;
    throw new PaymentRequestError(errorMessage(err, fallback), err?.response?.data?.code);
  }
}

/** A random id for idempotent submits; reused when the same dialog is submitted twice. */
export const newRequestKey = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('');

export const listPaymentRequests = (restaurantId: string, reservationId: string) =>
  request<PaymentRequest[]>(
    'get',
    `${base(restaurantId)}/reservations/${reservationId}`,
    undefined,
    'Zahlungslinks konnten nicht geladen werden'
  );

export const createPaymentRequest = (
  restaurantId: string,
  reservationId: string,
  input: CreatePaymentRequestInput
) =>
  request<PaymentRequest>(
    'post',
    `${base(restaurantId)}/reservations/${reservationId}`,
    input,
    'Zahlungslink konnte nicht erstellt werden'
  );

const action = <T = PaymentRequest>(
  restaurantId: string,
  requestId: string,
  path: string,
  body: unknown,
  fallback: string
) => request<T>('post', `${base(restaurantId)}/${requestId}/${path}`, body, fallback);

export const sendPaymentRequest = (
  restaurantId: string,
  requestId: string,
  input: {
    channel: 'email' | 'sms';
    deliveryKey: string;
    email?: string;
    phone?: string;
    acknowledgeUnknown?: boolean;
  }
) => action(restaurantId, requestId, 'send', input, 'Versand fehlgeschlagen');

export const copyPaymentRequestLink = (restaurantId: string, requestId: string) =>
  action<{ url: string }>(restaurantId, requestId, 'link', {}, 'Link konnte nicht geladen werden');

export const renewPaymentRequest = (
  restaurantId: string,
  requestId: string,
  input: { renewKey: string; expiresAt?: number | null; lines?: LineInput[]; channel?: 'email' | 'sms' }
) => action(restaurantId, requestId, 'renew', input, 'Erneuern fehlgeschlagen');

export const revokePaymentRequest = (restaurantId: string, requestId: string) =>
  action(restaurantId, requestId, 'revoke', {}, 'Widerrufen fehlgeschlagen');

export const reconcilePaymentRequest = (restaurantId: string, requestId: string) =>
  action(restaurantId, requestId, 'reconcile', {}, 'Abgleich fehlgeschlagen');

export const creditPaymentRequest = (
  restaurantId: string,
  requestId: string,
  input: { posReference: string; amountCents: number }
) => action(restaurantId, requestId, 'credit', input, 'Anrechnung fehlgeschlagen');

export const refundPaymentRequest = (
  restaurantId: string,
  requestId: string,
  input: {
    refundKey: string;
    attemptId?: string;
    full?: boolean;
    allocations?: { lineIndex: number; amountCents: number }[];
    description?: string;
  }
) => action(restaurantId, requestId, 'refunds', input, 'Erstattung fehlgeschlagen');

export const retryRefundNote = (restaurantId: string, requestId: string, refundId: string) =>
  action(restaurantId, requestId, `refunds/${refundId}/note`, {}, 'Stornobeleg fehlgeschlagen');

export const resolvePaymentRequestClarifications = (
  restaurantId: string,
  requestId: string,
  note: string
) => action(restaurantId, requestId, 'clarifications/resolve', { note }, 'Klärung fehlgeschlagen');
